import { ComponentPropsWithoutRef, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldError,
  FieldValues,
  Path,
} from "react-hook-form";
import { TextInput } from "@carbon/react";

type CarbonTextInputProps = ComponentPropsWithoutRef<typeof TextInput>;

/**
 * The shape every form in this codebase stores a chosen place as — read straight off
 * `NewSiteForm`'s `onSubmit` (`address: address?.formatted_address, googlePlaceId:
 * address?.place_id`), the two fields a site record actually persists. `AutocompleteField` holds
 * exactly this pair as ONE RHF value (see the component docstring for why), never the two as
 * separate fields.
 */
export interface AutocompleteFieldValue {
  address: string;
  googlePlaceId: string;
}

/**
 * The subset of Google's Places `PlaceResult` this component (and `SiteAutocomplete` before it)
 * actually reads. Deliberately loose/partial, same as `SiteAutocomplete`'s own `place: any` —
 * there is no `@types/google.maps` dependency in this repo, and Google's real return shape has
 * dozens of fields no call site here needs.
 */
export interface GooglePlaceResult {
  place_id?: string;
  formatted_address?: string;
  address_components?: unknown[];
  geometry?: unknown;
}

interface GoogleAutocompleteInstance {
  setFields(fields: string[]): void;
  addListener(eventName: string, handler: () => void): void;
  getPlace(): GooglePlaceResult;
}

interface WindowWithGoogle {
  google?: {
    maps: {
      places: {
        Autocomplete: new (
          input: HTMLInputElement
        ) => GoogleAutocompleteInstance;
      };
    };
  };
}

/**
 * Loads the Google Maps Places script exactly once per document, same as
 * `SiteAutocomplete.tsx`'s `loadAsyncScript` — copied rather than imported (see this file's
 * top-level docstring for why) but behaviourally identical: dedupes on the `<script src>`
 * already being in the document, resolves once `onload` fires, rejects on `onerror`.
 */
function loadGooglePlacesScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${src}"]`);
    if (existingScript) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load Google Maps script"));
    document.head.appendChild(script);
  });
}

export type AutocompleteFieldProps<TFieldValues extends FieldValues> = Omit<
  CarbonTextInputProps,
  | "id"
  | "labelText"
  | "invalid"
  | "invalidText"
  | "name"
  | "onChange"
  | "onBlur"
  | "value"
  | "defaultValue"
  | "ref"
> & {
  /** The RHF field name — also becomes the input's `id`, the same convention every sibling in
   * this layer uses. The stored value is the `{address, googlePlaceId}` pair above, not a bare
   * string. */
  name: Path<TFieldValues>;
  label: string;
  control: Control<TFieldValues>;
  /** Appends the translated `form.optional` marker to `label`. Same convention as
   * `TextField`/`PhoneField` — see `TextField`'s docstring for the full required/optional
   * rationale. */
  optional?: boolean;
  /** Called with the raw Google `PlaceResult` the moment a suggestion is chosen — in ADDITION to
   * (never instead of) the `{address, googlePlaceId}` pair committed to `field.value`. For a
   * consumer that needs something else off that same selection (`address_components` for a
   * country/city breakdown, `geometry` for a map) without re-deriving it from scratch. */
  onPlaceSelected?: (place: GooglePlaceResult) => void;
};

/**
 * A Google Places lookup bound to a single React Hook Form value via `Controller` — the fifth
 * sibling in this layer (`Form`/`TextField`/`SelectField`/`PhoneField`), same guarantees, no new
 * dialect: real `<label htmlFor>`, `aria-invalid`/`aria-errormessage` from `fieldState.error`,
 * and a value fully derived from `field.value` every render (`PhoneField`'s precedent for a
 * composite value — no local state that can drift from what RHF actually holds).
 *
 * ## Why `components/SiteAutocomplete/` still exists, and why this isn't a rewrite of it
 *
 * `SiteAutocomplete.tsx` (88 lines) is still consumed by
 * `containers/OrderByRequest/InlineCreateSiteModal` — a different, unmigrated, untested flow —
 * so it stays put. This component's Google integration (the script loader, the `Autocomplete`
 * constructor call, `setFields`, the `place_changed` listener, the `formatted_address`/
 * `place_id` fields it reads) is copied from it verbatim in behaviour, not imported: pulling a
 * shared util out from under a component another flow still depends on is exactly the kind of
 * change that risks that flow without this task touching it or its tests at all. What changes
 * here is ONLY the binding — Carbon `TextInput` instead of a raw styled `<input>`, RHF
 * `Controller` instead of a `setAddress` callback prop, a real label instead of a placeholder —
 * never the Places API calls themselves.
 *
 * ## Which accessibility world this is in — and why the suggestion list has no roles here
 *
 * `google.maps.places.Autocomplete` (the classic widget both this component and
 * `SiteAutocomplete` use) does not hand back place data for this component to render its own
 * suggestion list from. Constructing it attaches Google's OWN dropdown — a `.pac-container`/
 * `.pac-item` tree — directly to `document.body`, entirely outside React's render tree and this
 * component's control. That is the reference implementation's actual behaviour, confirmed by
 * reading it rather than assumed, and it is exactly why the current widget's suggestions have no
 * ARIA role or accessible name: those nodes are Google's DOM, not ours, and there is nothing in
 * this component's own render output to add a role to. `TextField`-style semantics apply fully
 * to the INPUT (real label, `aria-invalid`, `aria-errormessage`) but cannot extend to a
 * suggestion list this component never renders. A `getByRole("option", ...)` assertion is not
 * included in this file's tests for that reason — there is no such role to find, in this
 * component or in production. Making the suggestion list itself accessible would mean replacing
 * the classic widget with something that renders its own list in React (Carbon's `ComboBox` with
 * Places results fed in as options, for instance) — a materially different component, out of
 * scope here, and a decision for whoever owns the Sites e2e strategy next.
 */
function AutocompleteField<TFieldValues extends FieldValues>({
  name,
  label,
  control,
  optional,
  onPlaceSelected,
  ...rest
}: AutocompleteFieldProps<TFieldValues>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <AutocompleteInput
          {...rest}
          id={name}
          label={label}
          optional={optional}
          field={field}
          error={fieldState.error}
          onPlaceSelected={onPlaceSelected}
        />
      )}
    />
  );
}

export default AutocompleteField;

type AutocompleteInputProps<TFieldValues extends FieldValues> = Omit<
  CarbonTextInputProps,
  | "id"
  | "labelText"
  | "invalid"
  | "invalidText"
  | "onChange"
  | "onBlur"
  | "value"
> & {
  id: string;
  label: string;
  optional?: boolean;
  field: ControllerRenderProps<TFieldValues, Path<TFieldValues>>;
  error?: FieldError;
  onPlaceSelected?: (place: GooglePlaceResult) => void;
};

/**
 * The real function component doing the Places wiring — split out from `AutocompleteField` so
 * its `useRef`/`useEffect` belong to a component of their own rather than living inside
 * `Controller`'s `render` prop (which is `Controller`'s own render pass, not a separate
 * component — hooks have no business running there).
 */
function AutocompleteInput<TFieldValues extends FieldValues>({
  id,
  label,
  optional,
  field,
  error,
  onPlaceSelected,
  ...rest
}: AutocompleteInputProps<TFieldValues>) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);

  // `field.onChange`/`onPlaceSelected` read through refs so the Places widget is constructed
  // exactly once per mount (empty dependency array below) rather than being torn down and
  // rebuilt on every keystroke — RHF gives `field.onChange` a stable identity across renders in
  // practice, but reading through a ref removes any dependence on that being true.
  const onChangeRef = useRef(field.onChange);
  onChangeRef.current = field.onChange;
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  onPlaceSelectedRef.current = onPlaceSelected;

  useEffect(() => {
    const win = window as unknown as WindowWithGoogle;

    async function initMapScript(): Promise<void> {
      if (win.google) {
        return;
      }
      const src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE}&libraries=places`;
      await loadGooglePlacesScript(src);
    }

    function initAutocomplete() {
      if (!inputRef.current || !win.google) {
        return;
      }
      const autocomplete = new win.google.maps.places.Autocomplete(
        inputRef.current
      );
      // Same field list SiteAutocomplete requests — no reason for this binding to ask Google
      // for more than the reference implementation already does.
      autocomplete.setFields([
        "place_id",
        "formatted_address",
        "address_components",
        "geometry",
      ]);
      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        onChangeRef.current({
          address: place?.formatted_address ?? "",
          googlePlaceId: place?.place_id ?? "",
        } satisfies AutocompleteFieldValue);
        onPlaceSelectedRef.current?.(place);
      });
    }

    initMapScript().then(initAutocomplete).catch(console.error);
  }, []);

  const labelText = optional ? `${label} ${t("form.optional")}` : label;
  // `field.value`'s static type is a union over every path in TFieldValues (Path<TFieldValues>
  // isn't narrowed to this one field's value type) — this cast is the same unavoidable widening
  // DataTable's `row[header.key] as ReactNode` documents: every real call site's `TFieldValues`
  // already types this field as `AutocompleteFieldValue`, so the cast is on the type checker's
  // ability to prove it here, not on whether it's actually true.
  const value = field.value as AutocompleteFieldValue | undefined;

  return (
    <TextInput
      {...rest}
      id={id}
      ref={inputRef}
      labelText={labelText}
      value={value?.address ?? ""}
      onChange={(event) => {
        field.onChange({
          address: event.target.value,
          googlePlaceId: "",
        } satisfies AutocompleteFieldValue);
      }}
      onBlur={field.onBlur}
      invalid={Boolean(error)}
      invalidText={error?.message}
    />
  );
}
